import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AriaOrb } from "../components/AriaOrb";
import { toast } from "sonner";

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", organization: "", plan: "pro" });
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup(form);
      toast.success("Account created!");
      navigate("/app/chat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally { setBusy(false); }
  };

  const field = (key) => ({
    value: form[key],
    onChange: (e) => setForm({ ...form, [key]: e.target.value }),
  });

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 bg-[#18181B] text-white relative overflow-hidden">
        <div className="flex items-center gap-3"><AriaOrb size={36} /> <span className="font-serif text-2xl">ARIA</span></div>
        <div className="relative z-10 max-w-md">
          <h2 className="font-serif text-4xl leading-tight mb-4">Stand up your lab's AI in 60 seconds.</h2>
          <ul className="space-y-2 text-slate-300 text-sm">
            <li>• Upload PDFs · ARIA chunks & indexes instantly</li>
            <li>• Ask any protocol question by voice or text</li>
            <li>• Invite your team — full RBAC built in</li>
          </ul>
        </div>
        <div className="text-xs text-slate-400">© ARIA Labs</div>
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#2563FF]/20 blur-3xl" />
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-4" data-testid="signup-form">
          <div className="lg:hidden flex items-center gap-3 mb-2"><AriaOrb size={32} /><span className="font-serif text-xl">ARIA</span></div>
          <div>
            <h1 className="font-serif text-3xl mb-1">Create your lab</h1>
            <p className="text-sm text-[var(--aria-text-muted)]">Start your free trial. No credit card needed.</p>
          </div>
          {[
            ["name", "Your name", "text"],
            ["email", "Work email", "email"],
            ["password", "Password (6+)", "password"],
            ["organization", "Organization name", "text"],
          ].map(([k, label, type]) => (
            <div key={k} className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-[var(--aria-text-muted)]">{label}</label>
              <input data-testid={`signup-${k}`} {...field(k)} type={type} required minLength={k==='password'?6:undefined}
                className="w-full px-3 py-2.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)] focus:ring-2 focus:ring-[#2563FF] focus:border-transparent outline-none" />
            </div>
          ))}
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-[var(--aria-text-muted)]">Plan</label>
            <select data-testid="signup-plan" {...field("plan")}
              className="w-full px-3 py-2.5 rounded-md border border-[var(--aria-border)] bg-[var(--aria-surface)] focus:ring-2 focus:ring-[#2563FF] focus:border-transparent outline-none">
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <button data-testid="signup-submit" disabled={busy} type="submit"
            className="w-full py-2.5 rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE] disabled:opacity-50 font-medium">
            {busy ? "Creating…" : "Create account"}
          </button>
          <div className="text-sm text-center text-[var(--aria-text-muted)]">
            Already have an account? <Link to="/login" className="text-[#2563FF] hover:underline">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
